import { lazy, Suspense, useDeferredValue, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import NoSSR from 'react-no-ssr';

import { useSoyuzExperiment } from '@spotahome/soyuz/client';
import { useFiltersContext } from '@spotahome/ui-library/src/tenant/context/FiltersContext';

import { useFacetSearchContext } from '@spotahome/ui-library';

import FiltersHeader from '../FiltersHeader';

const Filters = lazy(() => import('../Filters'));

const FiltersWrapper = ({
  isFiltersHidden,
  onFiltersToggle,
  onCloseFilters,
  currency = undefined,
  maxPriceInCity,
  cityId,
  onFiltersUpdate,
  onFacetFiltersUpdate,
  onClearFilters,
  cardPaginatorRef,
  results
}) => {
  const { filters: contextFilters } = useFiltersContext();
  const { facetSearch } = useFacetSearchContext();
  const isApplyAllAtOnce =
    useSoyuzExperiment('ApplyAllFiltersAtOnce').get('behaviour') === 'variant';
  const [pendingFilters, setPendingFilters] = useState({});

  const effectiveFilters = isApplyAllAtOnce
    ? { ...contextFilters, ...pendingFilters }
    : contextFilters;

  const handleFiltersUpdate = useCallback(
    change => {
      if (isApplyAllAtOnce) {
        setPendingFilters(prev => ({ ...prev, ...change }));
      } else {
        onFiltersUpdate(change);
      }
      onFacetFiltersUpdate(change);
    },
    [isApplyAllAtOnce, onFiltersUpdate, onFacetFiltersUpdate]
  );

  const handleCloseFilters = useCallback(
    e => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (isApplyAllAtOnce && Object.keys(pendingFilters).length > 0) {
        onFiltersUpdate(pendingFilters);
        setPendingFilters({});
      }
      onCloseFilters(e);
    },
    [isApplyAllAtOnce, pendingFilters, onFiltersUpdate, onCloseFilters]
  );

  const handleClearFilters = useCallback(
    e => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (isApplyAllAtOnce) {
        setPendingFilters({});
      }
      onClearFilters();
    },
    [isApplyAllAtOnce, onClearFilters]
  );

  const handleFiltersToggle = useCallback(() => {
    if (isApplyAllAtOnce && Object.keys(pendingFilters).length > 0) {
      onFiltersUpdate(pendingFilters);
      setPendingFilters({});
    }

    onFiltersToggle();
  }, [isApplyAllAtOnce, pendingFilters, onFiltersUpdate, onFiltersToggle]);

  const deferredIsFiltersHidden = useDeferredValue(isFiltersHidden);
  const shouldShowContainer = !isFiltersHidden;
  const shouldShowContent = !deferredIsFiltersHidden;

  if (!shouldShowContainer) {
    return null;
  }

  const totalResults = cardPaginatorRef.current?.getTotalMarkers();

  return (
    <NoSSR>
      <div className="search__filters-full">
        {shouldShowContent && (
          <Suspense fallback={<div className="filters__loading" />}>
            <Filters
              currency={currency}
              maxPriceInCity={maxPriceInCity}
              filters={effectiveFilters}
              onFiltersUpdate={handleFiltersUpdate}
              cityId={cityId}
              onClearFilters={handleClearFilters}
              onCloseFilters={handleCloseFilters}
              totalResults={totalResults}
            />
          </Suspense>
        )}
      </div>
      <FiltersHeader
        results={results}
        citySlug={cityId}
        totalResults={facetSearch.totalCount}
        onHandleFiltersToggle={handleFiltersToggle}
        currency={currency}
        isFooterInSearchPage
      />
    </NoSSR>
  );
};

FiltersWrapper.propTypes = {
  isFiltersHidden: PropTypes.bool.isRequired,
  onFiltersToggle: PropTypes.func.isRequired,
  onCloseFilters: PropTypes.func.isRequired,
  currency: PropTypes.shape({ isoCode: PropTypes.string }),
  maxPriceInCity: PropTypes.number.isRequired,
  cityId: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onFacetFiltersUpdate: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  cardPaginatorRef: PropTypes.shape({
    current: PropTypes.object
  }).isRequired,
  results: PropTypes.number.isRequired
};

export default FiltersWrapper;
